<template>
  <div
    v-if="shouldRender"
    ref="chatPreview"
    class="floating-chat"
    :class="{ 'floating-chat--open': isOpen }"
  >
    <transition-group
      name="global-chat-notification"
      tag="div"
      class="global-chat-notifier"
    >
      <div
        v-for="notification in visibleNotifications"
        :key="notification.id"
        class="global-chat-notification"
        :class="`global-chat-notification--${notification.type || 'batepapo'}`"
        @click="openNotification(notification)"
      >
        <div
          class="global-chat-notification__avatar"
          :class="getNotificationAvatarClass(notification)"
        >
          <v-icon
            v-if="notification.type === 'chat'"
            size="26"
            color="white"
          >
            mdi-whatsapp
          </v-icon>
          <v-icon
            v-else-if="notification.icon"
            size="22"
            color="white"
          >
            {{ notification.icon }}
          </v-icon>
          <template v-else>
            {{ getInitials(notification.title) }}
          </template>
          
          <span 
            v-if="notification.type === 'chat'"
            class="global-chat-notification__avatar-badge global-chat-notification__avatar-badge--chat"
          >
            <v-icon size="10" color="white">mdi-whatsapp</v-icon>
          </span>
          <span 
            v-else
            class="global-chat-notification__avatar-badge global-chat-notification__avatar-badge--batepapo"
          >
            <v-icon size="10" color="white">mdi-message</v-icon>
          </span>
        </div>

        <div class="global-chat-notification__content">
          <div class="global-chat-notification__meta" :class="getNotificationMetaClass(notification)">
            <v-icon size="14" class="global-chat-notification__meta-icon">
              {{ getNotificationTypeIcon(notification) }}
            </v-icon>
            <span>{{ getNotificationTypeLabel(notification) }}</span>
          </div>
          <div class="global-chat-notification__title">
            {{ notification.title }}
          </div>
          <div
            v-if="notification.sender"
            class="global-chat-notification__sender"
          >
            {{ notification.sender }}
          </div>
          <div class="global-chat-notification__preview">
            {{ notification.preview }}
          </div>
        </div>

        <button
          class="global-chat-notification__close"
          type="button"
          title="Fechar"
          aria-label="Fechar notificacao"
          @click.stop="removeNotification(notification.id)"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
      </div>
    </transition-group>

    <transition name="floating-chat-panel">
      <section
        v-if="shouldShow && isOpen"
        class="floating-chat__panel"
        role="dialog"
        aria-label="Preview do bate-papo"
        aria-modal="false"
      >
        <header class="floating-chat__header">
          <div class="floating-chat__header-text">
            <span class="floating-chat__eyebrow">Mensagens</span>
            <strong class="floating-chat__title">Conversas recentes</strong>
          </div>

          <button
            type="button"
            class="floating-chat__icon-button"
            aria-label="Fechar preview do bate-papo"
            @click="closePanel"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <div class="floating-chat__status-bar">
          <v-select
            v-model="userStatus"
            :items="statusOptions"
            dense
            hide-details
            solo
            flat
            class="floating-chat__status-select"
            @change="updateUserStatus"
          >
            <template v-slot:selection="{ item }">
              <div class="floating-chat__status-chip">
                <v-icon size="15" :color="getStatusIcon(item.value).color">
                  {{ getStatusIcon(item.value).icon }}
                </v-icon>
                <span>{{ item.text }}</span>
              </div>
            </template>
            <template v-slot:item="{ item }">
              <div class="floating-chat__status-chip">
                <v-icon size="15" :color="getStatusIcon(item.value).color">
                  {{ getStatusIcon(item.value).icon }}
                </v-icon>
                <span>{{ item.text }}</span>
              </div>
            </template>
          </v-select>
        </div>

        <div class="floating-chat__body">
          <div
            v-if="loadingConversations"
            class="floating-chat__state"
            aria-live="polite"
          >
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="28"
              width="3"
            />
          </div>

          <template v-else-if="recentConversations.length">
            <button
              v-for="conversation in recentConversations"
              :key="conversation.id"
              type="button"
              class="floating-chat__conversation"
              :class="{
                'floating-chat__conversation--unread':
                  conversation.mensagensNaoLidas > 0,
              }"
              :aria-label="getConversationAriaLabel(conversation)"
              @click="openConversation(conversation)"
            >
              <div
                class="floating-chat__avatar"
                :class="getAvatarColor(conversation.id)"
              >
                {{ getInitials(conversation.nomeChat) }}
                <span
                  v-if="conversation.tipo === 'GRUPO'"
                  class="floating-chat__avatar-indicator"
                >
                  <v-icon size="10" color="white">mdi-account-group</v-icon>
                </span>
              </div>

              <div class="floating-chat__conversation-main">
                <div class="floating-chat__conversation-top">
                  <span class="floating-chat__conversation-name">
                    {{ conversation.nomeChat }}
                  </span>
                  <time class="floating-chat__time">
                    {{
                      conversation.ultimaMensagem
                        ? formatMessageDateTime(
                            conversation.ultimaMensagem.dataEnvio,
                          )
                        : ""
                    }}
                  </time>
                </div>

                <div class="floating-chat__conversation-bottom">
                  <div class="floating-chat__preview">
                    <div class="floating-chat__preview-content">
                      <v-icon
                        v-if="
                          conversation.ultimaMensagem &&
                          currentUser &&
                          conversation.ultimaMensagem.idRemetente ===
                            currentUser.id
                        "
                        size="14"
                        class="mr-1"
                        :color="
                          isMensagemLida(conversation.ultimaMensagem)
                            ? 'blue'
                            : 'grey'
                        "
                      >
                        {{
                          isMensagemLida(conversation.ultimaMensagem)
                            ? "mdi-check-all"
                            : "mdi-check"
                        }}
                      </v-icon>
                      <div class="floating-chat__preview-text-container">
                        <span
                          v-if="
                            conversation.tipo === 'GRUPO' &&
                            conversation.ultimaMensagem &&
                            currentUser &&
                            conversation.ultimaMensagem.idRemetente !==
                              currentUser.id
                          "
                          class="floating-chat__sender-name"
                        >
                          {{
                            getNomeRemetente(conversation.ultimaMensagem)
                          }}
                        </span>
                        <span
                          class="floating-chat__preview-text"
                          v-html="
                            conversation.ultimaMensagem
                              ? formatarMensagemPreview(
                                  conversation.ultimaMensagem,
                                )
                              : 'Sem mensagens'
                          "
                        ></span>
                      </div>
                    </div>

                    <span
                      v-if="conversation.mensagensNaoLidas > 0"
                      class="floating-chat__unread-dot"
                      aria-hidden="true"
                    >
                    </span>
                  </div>
                </div>
              </div>

              <span
                v-if="conversation.mensagensNaoLidas > 0"
                class="floating-chat__conversation-badge"
              >
                {{ formatUnreadCount(conversation.mensagensNaoLidas) }}
              </span>
            </button>
          </template>

          <div v-else class="floating-chat__state">
            <v-icon size="42" color="#94a3b8">mdi-message-text-outline</v-icon>
            <p>Nenhuma conversa encontrada.</p>
          </div>
        </div>

        <footer class="floating-chat__footer">
          <v-btn
            depressed
            block
            color="#4caf50"
            class="floating-chat__open-button white--text"
            @click="openFullChat"
          >
            Abrir bate-papo
          </v-btn>
        </footer>
      </section>
    </transition>

    <button
      v-if="shouldShow"
      type="button"
      class="floating-chat__trigger"
      :class="{ 'floating-chat__trigger--pulse': hasNewMessagePulse }"
      :aria-label="triggerAriaLabel"
      :aria-expanded="String(isOpen)"
      aria-haspopup="dialog"
      @click="togglePanel"
    >
      <span class="floating-chat__trigger-icon">
        <v-icon color="white" size="28">mdi-comment-account-outline</v-icon>
        <span v-if="totalUnread > 0" class="floating-chat__badge">
          {{ formattedTotalUnread }}
        </span>
      </span>
      <span class="floating-chat__trigger-text">Bate-papo</span>
    </button>
  </div>
</template>

<script>
import batepapo from "@/services/batepapo";

const OPEN_CONVERSATION_KEY = "batepapo_open_conversation_id";

export default {
  name: "GlobalChatNotifier",

  data() {
    return {
      currentUser: null,
      conversations: [],
      unreadTotal: 0,
      userStatus: "ONLINE",
      chatConfig: {
        modoNotificacao: "PREVIEW_E_SOM",
        exibirBotaoFlutuante: true,
        tempoPopupMs: 6000,
        volumeNotificacao: 80,
        configurado: false,
      },
      chatConfigLoaded: false,
      statusOptions: [
        { text: "Online", value: "ONLINE" },
        { text: "Ausente", value: "AUSENTE" },
        { text: "Ocupado", value: "OCUPADO" },
        { text: "Não perturbe", value: "NAO_PERTURBE" },
        { text: "Offline", value: "OFFLINE" },
      ],
      loadingConversations: false,
      isOpen: false,
      stompClient: null,
      currentSubscription: null,
      isConnected: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 8,
      reconnectInterval: 3000,
      reconnectTimer: null,
      pingInterval: null,
      conversationsRefreshTimer: null,
      conversationsRequestId: 0,
      syncConnectionPromise: null,
      lastSyncConnectionKey: null,
      lastSyncConnectionAt: 0,
      pulseTimer: null,
      hasNewMessagePulse: false,
      notifications: [],
      notificationTimers: {},
      recentMessageIds: new Set(),
    };
  },

  computed: {
    shouldRender() {
      return this.shouldShow || this.notifications.length > 0;
    },

    shouldShow() {
      return (
        this.hasChatPermission(this.currentUser) &&
        !this.isAuthRoute &&
        !this.isBatePapoRoute &&
        this.chatConfigLoaded &&
        this.chatConfig.exibirBotaoFlutuante !== false
      );
    },

    isAuthRoute() {
      return (
        this.$route &&
        (this.$route.path === "/login" || this.$route.path === "/cadastro")
      );
    },

    isBatePapoRoute() {
      return this.$route && this.$route.path === "/batepapo";
    },

    recentConversations() {
      return [...this.conversations]
        .sort((first, second) => {
          return (
            this.getConversationTimestamp(second) -
            this.getConversationTimestamp(first)
          );
        });
    },

    visibleNotifications() {
      if (this.isOpen || this.isDoNotDisturb) {
        return [];
      }

      return this.notifications.slice(0, 3);
    },

    isDoNotDisturb() {
      return this.userStatus === "NAO_PERTURBE";
    },

    shouldShowPreviewNotification() {
      return (
        this.chatConfig.modoNotificacao === "PREVIEW_E_SOM" ||
        this.chatConfig.modoNotificacao === "APENAS_PREVIEW"
      );
    },

    shouldPlayNotificationSound() {
      return (
        this.chatConfig.modoNotificacao === "PREVIEW_E_SOM" ||
        this.chatConfig.modoNotificacao === "APENAS_SOM"
      );
    },

    totalUnread() {
      return Number(this.unreadTotal || 0);
    },

    formattedTotalUnread() {
      return this.formatUnreadCount(this.totalUnread);
    },

    triggerAriaLabel() {
      if (this.totalUnread > 0) {
        return `Abrir preview do bate-papo, ${this.formattedTotalUnread} mensagens nao lidas`;
      }

      return "Abrir preview do bate-papo";
    },
  },

  watch: {
    "$route.path"() {
      this.syncConnection();
    },
  },

  mounted() {
    this.currentUser = this.getSessionUser();
    this.syncConnection();
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleKeydown);
    window.addEventListener(
      "chat-notification",
      this.handleChatNotification,
    );
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener(
      "chat-notification",
      this.handleChatNotification,
    );
    this.disconnectGlobalChat();
  },

  methods: {
    getSessionUser() {
      try {
        return JSON.parse(sessionStorage.getItem("user"));
      } catch (error) {
        return null;
      }
    },

    hasChatPermission(user) {
      return !!(
        user &&
        Array.isArray(user.roles) &&
        (user.roles.includes("ROLE_BATEPAPO") ||
          user.roles.includes("ROLE_ADMIN"))
      );
    },

    async syncConnection() {
      this.currentUser = this.getSessionUser();
      const syncKey = `${this.currentUser?.id || "anon"}|${
        this.$route?.path || ""
      }`;
      const now = Date.now();

      if (this.syncConnectionPromise) {
        return this.syncConnectionPromise;
      }

      if (
        this.lastSyncConnectionKey === syncKey &&
        now - this.lastSyncConnectionAt < 1000
      ) {
        return;
      }

      if (!this.hasChatPermission(this.currentUser) || this.isAuthRoute || this.isBatePapoRoute) {
        this.closePanel();
        this.disconnectGlobalChat();
        this.lastSyncConnectionKey = syncKey;
        this.lastSyncConnectionAt = now;
        return;
      }

      this.syncConnectionPromise = Promise.all([
        this.loadUnreadTotal(),
        this.consultarStatus(),
        this.obterConfiguracao(),
      ])
        .then(() => {
          if (this.chatConfig.exibirBotaoFlutuante === false) {
            this.closePanel();
            this.clearNotifications();
          }

          if (!this.isConnected && !this.stompClient) {
            this.connectGlobalChat();
          }

          this.lastSyncConnectionKey = syncKey;
          this.lastSyncConnectionAt = Date.now();
        })
        .finally(() => {
          this.syncConnectionPromise = null;
        });

      return this.syncConnectionPromise;
    },

    async obterConfiguracao() {
      try {
        const response = await batepapo.obterConfiguracao();
        this.chatConfig = {
          ...this.chatConfig,
          ...(response.data || {}),
        };
      } catch (error) {
        console.error("Erro ao carregar configuração do bate-papo:", error);
      } finally {
        this.chatConfigLoaded = true;
      }
    },

    async consultarStatus() {
      try {
        const response = await batepapo.consultarStatus();
        this.userStatus = response.data.status || "ONLINE";

        if (this.isDoNotDisturb) {
          this.clearNotifications();
        }
      } catch (error) {
        console.error("Erro ao consultar status do bate-papo:", error);
      }
    },

    async loadConversations() {
      if (!this.currentUser) {
        return;
      }

      const requestId = this.conversationsRequestId + 1;
      this.conversationsRequestId = requestId;

      try {
        this.loadingConversations = true;
        const response = await batepapo.listarConversas();

        if (requestId === this.conversationsRequestId) {
          this.conversations = response.data || [];
          this.unreadTotal = this.conversations.reduce((total, conversation) => {
            return total + Number(conversation.mensagensNaoLidas || 0);
          }, 0);
        }
      } catch (error) {
        console.error("Erro ao carregar conversas do preview:", error);
      } finally {
        if (requestId === this.conversationsRequestId) {
          this.loadingConversations = false;
        }
      }
    },

    async loadUnreadTotal() {
      if (!this.currentUser) {
        return;
      }

      try {
        const response = await batepapo.obterTotalMensagensNaoLidas();
        this.unreadTotal = Number(response.data?.total || 0);
      } catch (error) {
        console.error("Erro ao carregar total de mensagens nao lidas:", error);
      }
    },

    scheduleLoadConversations(delay = 450) {
      if (this.conversationsRefreshTimer) {
        clearTimeout(this.conversationsRefreshTimer);
      }

      this.conversationsRefreshTimer = setTimeout(() => {
        this.conversationsRefreshTimer = null;
        if (this.isOpen) {
          this.loadConversations();
        } else {
          this.loadUnreadTotal();
        }
      }, delay);
    },

    connectGlobalChat() {
      if (!this.currentUser) {
        return;
      }

      this.clearReconnectTimer();
      this.stompClient = batepapo.connectWebSocket({
        onConnect: (frame, client) => {
          this.isConnected = true;
          this.stompClient = client;
          this.reconnectAttempts = 0;
          console.debug("[BatePapo WS] conectado");
          this.subscribeToUser();
          this.startPingTimer();
        },
        onError: () => {
          console.debug("[BatePapo WS] erro/desconectado");
          this.isConnected = false;
          this.currentSubscription = null;
          this.stopPingTimer();
          this.scheduleReconnect();
        },
      });
    },

    subscribeToUser() {
      if (!this.stompClient || !this.stompClient.connected) {
        return;
      }

      this.clearSubscription();
      this.currentSubscription = batepapo.subscribeToUser(
        this.stompClient,
        this.handleUserPayload,
        this.handleReadPayload,
        this.handleStatusPayload,
      );
    },

    handleUserPayload(payload) {
      console.debug("[BatePapo WS][usuario] recebido", {
        payload,
        isConversation: batepapo.isChatConversationPayload(payload),
        isMessage: batepapo.isChatMessagePayload(payload),
        unreadTotal: this.unreadTotal,
        isOpen: this.isOpen,
      });

      if (batepapo.isChatConversationPayload(payload)) {
        this.upsertConversation(payload);
        this.scheduleLoadConversations();
        return;
      }

      if (!batepapo.isChatMessagePayload(payload)) {
        return;
      }

      if (payload.excluida) {
        this.notifications
          .filter((item) => item.messageId === payload.id)
          .forEach((item) => this.removeNotification(item.id));
        this.scheduleLoadConversations();
        return;
      }

      if (this.recentMessageIds.has(payload.id)) {
        console.debug("[BatePapo WS][usuario] mensagem duplicada ignorada", {
          messageId: payload.id,
          payload,
        });
        return;
      }

      this.rememberMessage(payload.id);
      this.applyIncomingMessage(payload);
      this.incrementUnreadTotal(payload);
      this.triggerFloatingPulse(payload);
      this.addOrUpdateNotification(payload);
      this.playAlertSound(payload);
      this.scheduleLoadConversations();
    },

    handleReadPayload(readPayload) {
      console.debug("[BatePapo WS][leitura] recebido", {
        payload: readPayload,
        isOpen: this.isOpen,
      });

      if (this.isOpen) {
        this.refreshConversation(readPayload.idConversa);
      }
      this.scheduleLoadConversations();
    },

    handleStatusPayload(payload) {
      console.debug("[BatePapo WS][status] recebido", payload);
      this.scheduleLoadConversations(0);
      this.consultarStatus();
    },

    rememberMessage(messageId) {
      this.recentMessageIds.add(messageId);

      if (this.recentMessageIds.size > 100) {
        const iterator = this.recentMessageIds.values();
        this.recentMessageIds.delete(iterator.next().value);
      }
    },

    applyIncomingMessage(message) {
      const index = this.conversations.findIndex(
        (conversation) => conversation.id === message.idConversa,
      );

      if (index === -1) {
        if (this.isOpen) {
          this.loadConversations();
        }
        return;
      }

      const current = this.conversations[index];
      const unreadIncrement =
        message.idRemetente !== this.currentUser.id ? 1 : 0;

      this.$set(this.conversations, index, {
        ...current,
        ultimaMensagem: message,
        mensagensNaoLidas:
          Number(current.mensagensNaoLidas || 0) + unreadIncrement,
      });

    },

    incrementUnreadTotal(message) {
      if (
        !this.currentUser ||
        !message ||
        message.idRemetente === this.currentUser.id
      ) {
        return;
      }

      this.unreadTotal = Number(this.unreadTotal || 0) + 1;
    },

    upsertConversation(conversation) {
      if (!conversation || !conversation.id) return;

      const index = this.conversations.findIndex(
        (item) => item.id === conversation.id,
      );

      if (index === -1) {
        this.conversations.unshift(conversation);
      } else {
        this.$set(this.conversations, index, {
          ...this.conversations[index],
          ...conversation,
        });
      }
    },

    refreshConversation(conversationId) {
      if (!conversationId) {
        return;
      }

      batepapo
        .obterConversa(conversationId)
        .then((response) => {
          this.upsertConversation(response.data);
        })
        .catch((error) => {
          console.error("Erro ao atualizar conversa do preview:", error);
        });
    },

    addOrUpdateNotification(message) {
      if (
        !this.currentUser ||
        !this.shouldShowPreviewNotification ||
        this.isDoNotDisturb ||
        message.idRemetente === this.currentUser.id ||
        message.tipo === "SISTEMA" ||
        message.tipo === "DATA"
      ) {
        return;
      }

      const notification = {
        id: `batepapo-${message.idConversa}`,
        type: "batepapo",
        idConversa: message.idConversa,
        messageId: message.id,
        title: message.nomeConversa || message.nomeRemetente || "Nova mensagem",
        sender: "",
        preview: this.getMessagePreview(message),
        timestamp: Date.now(),
      };

      const index = this.notifications.findIndex(
        (item) => item.id === notification.id,
      );

      if (index === -1) {
        this.notifications.unshift(notification);
      } else {
        this.$set(this.notifications, index, notification);
        this.notifications.sort((first, second) => {
          return second.timestamp - first.timestamp;
        });
      }

      this.notifications = this.notifications.slice(0, 3);
      this.resetNotificationTimer(notification.id);
      this.updateNotificationConversationData(notification.idConversa, message);
    },

    handleChatNotification(event) {
      const detail = event?.detail || {};
      const protocolNumber = detail.protocolNumber || detail.protocolo;

      if (!protocolNumber || !this.shouldShowPreviewNotification || this.isDoNotDisturb) {
        return;
      }

      const notification = {
        id: `chat-${protocolNumber}`,
        type: "chat",
        protocolNumber,
        title: detail.title || `Protocolo ${protocolNumber}`,
        sender: detail.sender || "Chat",
        preview: detail.preview || "Nova mensagem",
        icon: detail.icon || "mdi-whatsapp",
        route: {
          path: "/v2chat",
          query: {
            protocolo: protocolNumber,
            empresa: detail.empresa || undefined,
            telefoneEmpresa: detail.telefoneEmpresa || undefined,
          },
        },
        timestamp: Date.now(),
      };

      const index = this.notifications.findIndex(
        (item) => item.id === notification.id,
      );

      if (index === -1) {
        this.notifications.unshift(notification);
      } else {
        this.$set(this.notifications, index, notification);
        this.notifications.sort((first, second) => {
          return second.timestamp - first.timestamp;
        });
      }

      this.notifications = this.notifications.slice(0, 3);
      this.resetNotificationTimer(notification.id);
    },

    updateNotificationConversationData(conversationId, message) {
      batepapo
        .obterConversa(conversationId)
        .then((response) => {
          const conversation = response.data;
          const index = this.notifications.findIndex(
            (item) => item.idConversa === conversationId,
          );

          if (index === -1 || !conversation || !conversation.nomeChat) {
            return;
          }

          this.$set(this.notifications, index, {
            ...this.notifications[index],
            title: conversation.nomeChat,
            sender:
              conversation.tipo === "GRUPO"
                ? this.getNotificationSender(message)
                : "",
          });
        })
        .catch((error) => {
          console.error("Erro ao carregar conversa para notificacao:", error);
        });
    },

    getNotificationSender(message) {
      if (!message.nomeRemetente) {
        return "";
      }

      return message.nomeRemetente;
    },

    resetNotificationTimer(notificationId) {
      if (this.notificationTimers[notificationId]) {
        clearTimeout(this.notificationTimers[notificationId]);
      }

      this.$set(
        this.notificationTimers,
        notificationId,
        setTimeout(() => {
          this.removeNotification(notificationId);
        }, this.chatConfig.tempoPopupMs || 6000),
      );
    },

    removeNotification(notificationId) {
      if (this.notificationTimers[notificationId]) {
        clearTimeout(this.notificationTimers[notificationId]);
        this.$delete(this.notificationTimers, notificationId);
      }

      this.notifications = this.notifications.filter(
        (notification) => notification.id !== notificationId,
      );
    },

    openNotification(notification) {
      if (!notification) {
        return;
      }

      if (notification.type === "chat") {
        this.removeNotification(notification.id);
        this.closePanel();

        if (notification.route) {
          this.$router.push(notification.route).catch(() => {});
        }

        return;
      }

      if (!notification.idConversa) {
        return;
      }

      sessionStorage.setItem(OPEN_CONVERSATION_KEY, String(notification.idConversa));
      this.removeNotification(notification.id);
      this.closePanel();

      if (this.$route.path !== "/batepapo") {
        this.$router.push("/batepapo");
      }
    },

    openConversation(conversation) {
      if (!conversation || !conversation.id) {
        return;
      }

      sessionStorage.setItem(OPEN_CONVERSATION_KEY, String(conversation.id));
      this.closePanel();

      if (this.$route.path !== "/batepapo") {
        this.$router.push("/batepapo");
      }
    },

    togglePanel() {
      if (this.isOpen) {
        this.closePanel();
        return;
      }

      this.isOpen = true;
      this.loadConversations();
    },

    closePanel() {
      this.isOpen = false;
    },

    openFullChat() {
      this.closePanel();

      if (this.$route.path !== "/batepapo") {
        this.$router.push("/batepapo");
      }
    },

    updateUserStatus() {
      if (!this.isConnected || !this.stompClient) {
        console.warn("WebSocket não conectado, não foi possível atualizar status do usuário");
        this.consultarStatus();
        return false;
      }

      const success = batepapo.updateUserStatusViaWebSocket(
        this.stompClient,
        this.userStatus,
      );

      this.consultarStatus();

      return success;
    },

    handleOutsideClick(event) {
      if (!this.isOpen || !this.$refs.chatPreview) {
        return;
      }

      if (!this.$refs.chatPreview.contains(event.target)) {
        this.closePanel();
      }
    },

    handleKeydown(event) {
      if (event.key === "Escape" && this.isOpen) {
        this.closePanel();
      }
    },

    getConversationTimestamp(conversation) {
      if (!conversation || !conversation.ultimaMensagem) {
        return 0;
      }

      return new Date(conversation.ultimaMensagem.dataEnvio).getTime() || 0;
    },

    formatUnreadCount(count) {
      const value = Number(count || 0);
      return value > 99 ? "99+" : String(value);
    },

    formatMessageDateTime(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      const today = new Date();

      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      return date.toLocaleDateString();
    },

    getInitials(name) {
      if (!name) return "";

      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },

    getAvatarColor(index) {
      const colors = [
        "floating-chat__avatar--green",
        "floating-chat__avatar--blue",
        "floating-chat__avatar--teal",
        "floating-chat__avatar--orange",
      ];

      return colors[Math.abs(index || 0) % colors.length];
    },

    getNotificationAvatarClass(notification) {
      if (notification?.type === "chat") {
        return "global-chat-notification__avatar--chat";
      }

      return this.getAvatarColor(notification?.idConversa);
    },

    getNotificationMetaClass(notification) {
      return notification?.type === "chat" 
        ? "global-chat-notification__meta--chat"
        : "global-chat-notification__meta--batepapo";
    },

    getNotificationTypeIcon(notification) {
      return notification?.type === "chat"
        ? "mdi-whatsapp"
        : "mdi-comment-account-outline";
    },

    getNotificationTypeLabel(notification) {
      return notification?.type === "chat" ? "WhatsApp" : "Bate-papo";
    },

    getNomeRemetente(message) {
      if (!message) {
        return null;
      }

      if (message.nomeRemetente) {
        return `${message.nomeRemetente}:`;
      }

      if (message.tipo === "SISTEMA") {
        return null;
      }

      return "Usuário:";
    },

    getMessagePreview(message) {
      if (!message) {
        return "Sem mensagens";
      }

      if (message.excluida) {
        return "Mensagem apagada";
      }

      if (message.tipo === "ARQUIVO") {
        return `Arquivo: ${message.nomeArquivo || "arquivo recebido"}`;
      }

      if (message.tipo === "SISTEMA") {
        return "Mensagem do sistema";
      }

      return this.truncatePlainText(message.conteudo || "Nova mensagem", 86);
    },

    isMensagemLida(mensagem) {
      return Boolean(
        mensagem &&
          (mensagem.lida || String(mensagem.status || "").toUpperCase() === "LIDA"),
      );
    },

    formatarMensagemPreview(mensagem) {
      if (!mensagem) {
        return "Sem mensagens";
      }

      if (mensagem.tipo === "ARQUIVO" && mensagem.nomeArquivo) {
        let icone = "mdi-file-outline";

        if (this.isAudio(mensagem.tipoArquivo)) {
          icone = "mdi-microphone";
          return `<i class="v-icon mdi ${icone}" style="font-size: 14px; vertical-align: middle; margin-right: 4px;"></i> ${this.escapeHtml(
            mensagem.nomeArquivo,
          )}`;
        }

        if (
          this.isPreview(mensagem.nomeArquivo) &&
          !this.isAudio(mensagem.tipoArquivo)
        ) {
          icone = "mdi-file-image-outline";
        }

        return `<i class="v-icon mdi ${icone}" style="font-size: 14px; vertical-align: middle; margin-right: 4px;"></i> ${this.escapeHtml(
          this.truncatePlainText(mensagem.nomeArquivo, 25, true),
        )}`;
      }

      if (!mensagem.conteudo) {
        return "";
      }

      let texto = this.escapeHtml(mensagem.conteudo);

      texto = texto.replace(/\n/g, " ");
      texto = texto.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      texto = texto.replace(/_(.*?)_/g, "<em>$1</em>");
      texto = texto.replace(/__(.*?)__/g, "<u>$1</u>");

      return texto;
    },

    escapeHtml(texto) {
      const div = document.createElement("div");
      div.textContent = texto;
      return div.innerHTML;
    },

    truncatePlainText(text, limit, preserveEnds = false) {
      const normalized = String(text || "").replace(/\s+/g, " ").trim();

      if (normalized.length <= limit) {
        return normalized;
      }

      if (preserveEnds) {
        const ellipsis = "...";
        const available = limit - ellipsis.length;
        const startLength = Math.ceil(available / 2);
        const endLength = Math.floor(available / 2);

        return (
          normalized.slice(0, startLength) +
          ellipsis +
          normalized.slice(normalized.length - endLength)
        );
      }

      return `${normalized.substring(0, limit)}...`;
    },

    isPreview(nomeArquivo) {
      if (!nomeArquivo) return false;

      try {
        const extension = nomeArquivo.split(".").pop().toLowerCase();
        return [
          "jpg",
          "jpeg",
          "png",
          "gif",
          "webp",
          "svg",
          "mp4",
          "pdf",
        ].includes(extension);
      } catch (e) {
        return false;
      }
    },

    isAudio(tipoArquivo) {
      if (!tipoArquivo) return false;

      if (tipoArquivo.includes("/")) {
        return tipoArquivo.startsWith("audio/");
      }

      const ext = tipoArquivo.toLowerCase();
      return ["mp3", "wav", "ogg", "m4a", "aac", "flac", "webm"].includes(ext);
    },

    getConversationAriaLabel(conversation) {
      const unread = Number(conversation.mensagensNaoLidas || 0);
      const unreadText = unread
        ? `, ${this.formatUnreadCount(unread)} mensagens nao lidas`
        : "";

      return `Visualizar conversa ${conversation.nomeChat}${unreadText}`;
    },

    getStatusIcon(status) {
      switch (status) {
        case "ONLINE":
          return { icon: "mdi-check-circle", color: "#4caf50" };
        case "OFFLINE":
          return { icon: "mdi-close-circle", color: "#9e9e9e" };
        case "OCUPADO":
          return { icon: "mdi-circle", color: "#f44336" };
        case "NAO_PERTURBE":
          return { icon: "mdi-minus-circle", color: "#f44336" };
        case "AUSENTE":
          return { icon: "mdi-clock-time-five", color: "#ffc107" };
        default:
          return { icon: "mdi-help-circle", color: "#9e9e9e" };
      }
    },

    playAlertSound(message) {
      if (
        !this.currentUser ||
        !message ||
        !this.shouldPlayNotificationSound ||
        this.isDoNotDisturb ||
        message.idRemetente === this.currentUser.id ||
        message.tipo === "SISTEMA" ||
        message.tipo === "DATA"
      ) {
        return;
      }

      try {
        const audio = new Audio(require("@/assets/sounds/alerta.wav"));
        audio.volume = this.getNotificationVolume();
        audio.play();
      } catch (error) {
        console.error("Erro ao reproduzir som de alerta:", error);
      }
    },

    getNotificationVolume() {
      const volume = Number(this.chatConfig.volumeNotificacao || 80);
      const normalizedVolume = Math.min(Math.max(volume, 30), 100);

      return normalizedVolume / 100;
    },

    triggerFloatingPulse(message) {
      if (
        !this.currentUser ||
        !message ||
        !this.shouldShow ||
        message.idRemetente === this.currentUser.id ||
        message.tipo === "SISTEMA" ||
        message.tipo === "DATA"
      ) {
        return;
      }

      if (this.pulseTimer) {
        clearTimeout(this.pulseTimer);
      }

      this.hasNewMessagePulse = false;

      this.$nextTick(() => {
        this.hasNewMessagePulse = true;
        this.pulseTimer = setTimeout(() => {
          this.hasNewMessagePulse = false;
          this.pulseTimer = null;
        }, 1800);
      });
    },

    startPingTimer() {
      this.stopPingTimer();
      batepapo.sendPing(this.stompClient);
      this.pingInterval = setInterval(() => {
        if (this.isConnected && this.stompClient) {
          batepapo.sendPing(this.stompClient);
        }
      }, 60000);
    },

    stopPingTimer() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },

    scheduleReconnect() {
      if (!this.shouldShow) {
        return;
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        return;
      }

      this.reconnectAttempts += 1;
      const delay =
        this.reconnectInterval * Math.min(this.reconnectAttempts, 3);

      this.clearReconnectTimer();
      this.reconnectTimer = setTimeout(() => {
        this.disconnectSocketOnly();
        this.connectGlobalChat();
      }, delay);
    },

    clearReconnectTimer() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    },

    clearConversationsRefreshTimer() {
      if (this.conversationsRefreshTimer) {
        clearTimeout(this.conversationsRefreshTimer);
        this.conversationsRefreshTimer = null;
      }
    },

    clearPulseTimer() {
      if (this.pulseTimer) {
        clearTimeout(this.pulseTimer);
        this.pulseTimer = null;
      }

      this.hasNewMessagePulse = false;
    },

    clearSubscription() {
      if (this.currentSubscription) {
        Object.values(this.currentSubscription).forEach((subscription) => {
          if (subscription && subscription.unsubscribe) {
            subscription.unsubscribe();
          }
        });
      }

      this.currentSubscription = null;
    },

    clearNotifications() {
      Object.keys(this.notificationTimers).forEach((key) => {
        clearTimeout(this.notificationTimers[key]);
      });

      this.notificationTimers = {};
      this.notifications = [];
    },

    disconnectSocketOnly() {
      this.clearSubscription();
      if (this.stompClient) {
        batepapo.disconnectWebSocket(this.stompClient);
      }
      this.stompClient = null;
      this.isConnected = false;
    },

    disconnectGlobalChat() {
      this.clearReconnectTimer();
      this.clearConversationsRefreshTimer();
      this.clearPulseTimer();
      this.stopPingTimer();
      this.disconnectSocketOnly();
      this.clearNotifications();
      this.reconnectAttempts = 0;
    },
  },
};
</script>

<style scoped>
.floating-chat {
  bottom: calc(24px + env(safe-area-inset-bottom));
  position: fixed;
  right: calc(24px + env(safe-area-inset-right));
  z-index: 3000;
}

.global-chat-notifier {
  bottom: 88px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
  position: absolute;
  right: 0;
  width: min(380px, calc(100vw - 32px));
}

.global-chat-notification {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
  cursor: pointer;
  display: flex;
  gap: 14px;
  min-height: 88px;
  padding: 14px 42px 14px 14px;
  pointer-events: auto;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.global-chat-notification:hover {
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.24);
  transform: translateY(-2px);
}

.global-chat-notification--chat {
  border-color: #93c5fd;
  border-left-width: 4px;
}

.global-chat-notification--batepapo {
  border-color: #86efac;
  border-left-width: 4px;
}

.global-chat-notification__avatar {
  align-items: center;
  border-radius: 12px;
  color: #ffffff;
  display: flex;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 700;
  height: 52px;
  justify-content: center;
  position: relative;
  width: 52px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.global-chat-notification__avatar--chat {
  background: linear-gradient(135deg, #0284c7 0%, #0ea5e9 100%);
}

.global-chat-notification__avatar-badge {
  align-items: center;
  border: 2px solid #ffffff;
  border-radius: 50%;
  bottom: -3px;
  display: flex;
  height: 20px;
  justify-content: center;
  position: absolute;
  right: -3px;
  width: 20px;
}

.global-chat-notification__avatar-badge--chat {
  background: #0284c7;
}

.global-chat-notification__avatar-badge--batepapo {
  background: #16a34a;
}

.global-chat-notification__content {
  min-width: 0;
}

.global-chat-notification__meta {
  align-items: center;
  display: flex;
  font-size: 11px;
  font-weight: 800;
  gap: 5px;
  line-height: 1;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.global-chat-notification__meta--chat {
  color: #0284c7;
}

.global-chat-notification__meta--batepapo {
  color: #16a34a;
}

.global-chat-notification__meta-icon {
  margin-right: 0 !important;
}

.global-chat-notification__title {
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-chat-notification__sender {
  color: #16a34a;
  font-size: 13px;
  font-weight: 600;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-chat-notification--chat .global-chat-notification__sender {
  color: #0284c7;
}

.global-chat-notification--batepapo .global-chat-notification__sender {
  color: #16a34a;
}

.global-chat-notification__preview {
  color: #475569;
  font-size: 13px;
  line-height: 1.4;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.global-chat-notification__close {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 10px;
  top: 10px;
  transition: background 0.2s ease, color 0.2s ease;
  width: 28px;
}

.global-chat-notification__close:hover {
  background: #f1f5f9;
  color: #475569;
}

.global-chat-notification--chat .global-chat-notification__close:hover {
  background: #dbeafe;
  color: #0284c7;
}

.global-chat-notification--batepapo .global-chat-notification__close:hover {
  background: #dcfce7;
  color: #16a34a;
}

.global-chat-notification-enter-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.global-chat-notification-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.global-chat-notification-enter {
  opacity: 0;
  transform: translateX(24px) scale(0.95);
}

.global-chat-notification-leave-to {
  opacity: 0;
  transform: translateX(16px) scale(0.98);
}

.floating-chat__trigger {
  align-items: center;
  background: transparent;
  border: 0;
  color: #1e293b;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
}

.floating-chat__trigger:focus-visible,
.floating-chat__conversation:focus-visible,
.floating-chat__icon-button:focus-visible {
  outline: 3px solid rgba(76, 175, 80, 0.32);
  outline-offset: 3px;
}

.floating-chat__trigger-icon {
  align-items: center;
  background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
  border-radius: 18px;
  box-shadow: 0 12px 28px rgba(76, 175, 80, 0.35);
  display: flex;
  height: 58px;
  justify-content: center;
  position: relative;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 58px;
}

.floating-chat__trigger:hover .floating-chat__trigger-icon,
.floating-chat--open .floating-chat__trigger-icon {
  box-shadow: 0 14px 34px rgba(76, 175, 80, 0.42);
  transform: translateY(-2px) scale(1.03);
}

.floating-chat__trigger--pulse .floating-chat__trigger-icon {
  animation: floating-chat-subtle-pulse 0.9s ease-in-out 2;
}

@keyframes floating-chat-subtle-pulse {
  0%,
  100% {
    box-shadow: 0 12px 28px rgba(76, 175, 80, 0.35);
    transform: scale(1);
  }

  45% {
    box-shadow: 0 14px 34px rgba(76, 175, 80, 0.48);
    transform: scale(1.045);
  }
}

.floating-chat__trigger-text {
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
}

.floating-chat__badge,
.floating-chat__conversation-badge {
  align-items: center;
  background: #ef4444;
  border: 2px solid #ffffff;
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  height: 22px;
  justify-content: center;
  line-height: 1;
  min-width: 22px;
  padding: 0 6px;
}

.floating-chat__badge {
  position: absolute;
  right: -8px;
  top: -8px;
}

.floating-chat__panel {
  background: #ffffff;
  border: 1px solid #dbe4ee;
  border-radius: 16px;
  bottom: 82px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  max-height: min(70vh, 620px);
  overflow: hidden;
  position: absolute;
  right: 0;
  width: min(380px, calc(100vw - 32px));
}

.floating-chat__header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  gap: 10px;
  padding: 14px 16px;
}

.floating-chat__header-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.floating-chat__eyebrow {
  color: #4caf50;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.floating-chat__title {
  color: #1e293b;
  font-size: 16px;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.floating-chat__icon-button {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 34px;
  justify-content: center;
  padding: 0;
  transition: background 0.2s ease, color 0.2s ease;
  width: 34px;
}

.floating-chat__icon-button:hover {
  background: #e8f5e9;
  color: #4caf50;
}

.floating-chat__status-bar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 16px;
}

.floating-chat__status-select {
  border: 1px solid #e2e8f0 !important;
  border-radius: 10px !important;
  font-size: 13px !important;
}

.floating-chat__status-select >>> .v-input__slot {
  background: #f8fafc !important;
  box-shadow: none !important;
  min-height: 36px !important;
}

.floating-chat__status-chip {
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
}

.floating-chat__body {
  background: #f8fafc;
  flex: 1;
  min-height: 220px;
  overflow-y: auto;
  padding: 10px;
}

.floating-chat__body::-webkit-scrollbar {
  width: 6px;
}

.floating-chat__body::-webkit-scrollbar-track {
  background: transparent;
}

.floating-chat__body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.floating-chat__conversation {
  align-items: center;
  background: #ffffff;
  border: 1px solid transparent;
  border-radius: 14px;
  cursor: pointer;
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
  min-height: 74px;
  padding: 11px 12px;
  position: relative;
  text-align: left;
  transition: background 0.2s ease, border-color 0.2s ease;
  width: 100%;
}

.floating-chat__conversation:hover {
  background: #f1f5f9;
}

.floating-chat__avatar {
  align-items: center;
  border-radius: 14px;
  color: #ffffff;
  display: flex;
  flex-shrink: 0;
  font-size: 15px;
  font-weight: 800;
  height: 50px;
  justify-content: center;
  position: relative;
  text-transform: uppercase;
  width: 50px;
}

.floating-chat__avatar--green {
  background: linear-gradient(135deg, #4caf50 0%, #81c784 100%);
}

.floating-chat__avatar--blue {
  background: linear-gradient(135deg, #2196f3 0%, #64b5f6 100%);
}

.floating-chat__avatar--teal {
  background: linear-gradient(135deg, #00897b 0%, #4db6ac 100%);
}

.floating-chat__avatar--orange {
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
}

.floating-chat__avatar-indicator {
  align-items: center;
  background: #4caf50;
  border-radius: 6px;
  bottom: -2px;
  display: flex;
  height: 19px;
  justify-content: center;
  position: absolute;
  right: -2px;
  width: 19px;
}

.floating-chat__conversation-main {
  flex: 1;
  min-width: 0;
}

.floating-chat__conversation-top,
.floating-chat__conversation-bottom {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
}

.floating-chat__conversation-name {
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.floating-chat__time {
  color: #94a3b8;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
}

.floating-chat__preview {
  align-items: center;
  color: #64748b;
  display: flex;
  flex: 1;
  font-size: 13px;
  gap: 8px;
  justify-content: space-between;
  line-height: 18px;
  min-width: 0;
}

.floating-chat__preview-content {
  align-items: center;
  display: flex;
  flex: 1;
  height: 18px;
  min-width: 0;
}

.floating-chat__preview-content .v-icon {
  align-self: center;
  flex: 0 0 14px;
  height: 14px;
  line-height: 14px;
  margin-top: 0;
}

.floating-chat__preview-text-container {
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 0.1rem;
}

.floating-chat__sender-name {
  color: #4caf50;
  font-weight: 800;
  margin-right: 3px;
  flex-shrink: 0;
}

.floating-chat__preview-text {
  color: #64748b;
  flex: 1;
  font-size: 13px;
  line-height: 18px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.floating-chat__conversation-badge {
  bottom: 9px;
  position: absolute;
  right: 9px;
}

.floating-chat__state {
  align-items: center;
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 12px;
  min-height: 220px;
  justify-content: center;
  text-align: center;
}

.floating-chat__state p {
  margin: 0;
}

.floating-chat__footer {
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 12px;
}

.floating-chat__open-button {
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  text-transform: none;
}

.floating-chat-panel-enter-active,
.floating-chat-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.floating-chat-panel-enter,
.floating-chat-panel-leave-to {
  opacity: 0;
  transform: translate(12px, 14px) scale(0.96);
}

@media (max-width: 600px) {
  .floating-chat {
    bottom: calc(16px + env(safe-area-inset-bottom));
    right: calc(16px + env(safe-area-inset-right));
  }

  .global-chat-notifier {
    bottom: 72px;
    width: min(360px, calc(100vw - 32px));
  }

  .floating-chat__trigger {
    min-height: 56px;
    min-width: 56px;
  }

  .floating-chat__trigger-icon {
    border-radius: 18px;
    height: 56px;
    width: 56px;
  }

  .floating-chat__trigger-text {
    display: none;
  }

  .floating-chat__panel {
    bottom: 72px;
    max-height: 70vh;
    right: 0;
    width: min(90vw, 390px);
  }

  .floating-chat__conversation {
    min-height: 76px;
  }
}
</style>
